// sim/apex.ts — APEX CREATURES: rare, single-at-a-time ecosystem BOSSES.
//
// An apex creature is a KIND.Apex entity that lives on the wildlife/ecosystem
// layer. It is neither a colony member nor ordinary wildlife: it owns its OWN
// combat path (Soldiers attack only rival colonies, so a boss would otherwise be
// untouchable) and its OWN defeat economy. Four bosses, each with a distinct
// behaviour and a distinct ecological impact (each a giant of a wildlife species):
//
//   Huntsman      (ground)  wide-ranging ground hunter of colony organisms
//   CrowMatriarch (flying)  wide-ranging aerial hunter (ignores walls)
//   RatKing       (ground)  hunts AND summons rat reinforcements (swells wildlife)
//   PondLeviathan (ground)  slow + enormously tanky; devours nearby wildlife
//
// ECOSYSTEM FORCE, NOT A COLONY ASSASSIN: a boss holds a TERRITORY (a scored,
// food/wildlife/contested-rich, nest-avoiding region) and runs a MODE state
// machine (Patrol / Hunt / Retreat / Migrate). It hunts WILDLIFE freely but a
// colony organism only when that colony is a SIGNIFICANT ecosystem actor AND the
// target is inside the territory; it RETREATS after a kill (no nest chain-wipe),
// MIGRATES on a slow cadence to reshape a new area, and is FORCE-migrated if it
// lingers near a nest — an apex can never camp a player nest indefinitely.
//
// DEFEAT ECONOMY: while a boss lives, colony Soldiers within its vulnRadius strip
// hp from it (config.soldierDamage each, per tick), and that damage is attributed
// per colony. When hp hits 0 the colony that dealt the MOST damage (ties → lowest
// id) loots a UNIQUE gene via awardGeneFromEvent (high qualityBonus ⇒ high rarity).
// A boss never starves; if undefeated after maxLifespan ticks it ESCAPES. A
// BESIEGED boss (took soldier damage in the last 2 ticks) HOLDS GROUND — it does
// not flee a soldier swarm — so the "out-damage the boss to defeat it" invariant
// is preserved.
//
// SINGLE-BOSS MODEL: at most ONE boss is alive at a time, so all per-boss state is
// a handful of scalars (no per-entity apex columns needed). Spawns are RARE and
// gated on a cadence + a low percent roll.
//
// DETERMINISM: pure integer state (exact FNV hashing), fixed iteration (box scans
// visit tiles in (y,x) order, ties break on lower id), RNG only from the apex
// stream passed in. Allocation-free: preallocated ring buffers + scalars, no
// per-tick temp objects/closures. Sensing = bounded Chebyshev box scans.

import {
  DIR,
  DIR_DX,
  DIR_DY,
  DIR_COUNT,
  NIL,
  KIND,
  CASTE,
  SPECIES,
  APEX_KIND,
  APEX_KIND_COUNT,
  APEX_MODE,
  APEX_OUTCOME,
} from '../types.ts';
import type {
  EntityId,
  Tick,
  System,
  ApexConfig,
  ApexWorldConfig,
  ApexCreature,
  ColonyId,
  Caste,
} from '../types.ts';
import type { EntitySystem } from '../engine/EntitySystem.ts';
import type { WorldGrid } from '../engine/WorldGrid.ts';
import type { Rng } from '../rng.ts';
import type { GreedyPathing } from '../engine/Pathing.ts';
import { chebyshev } from '../engine/Pathing.ts';
import type { ColonySystem } from './colony.ts';
import type { GeneInventory } from './genes.ts';
import { awardGeneFromEvent } from './geneForge.ts';
import type { WildlifeSystem } from './wildlife.ts';
import type { TimescaleConfig } from './timescale.ts';
import { LAYER, layerPeriod } from './timescale.ts';
import { apexScaledHp } from './enemyScaling.ts';

/** Sentinel "no apex kind" (used when no boss is live). */
const NO_KIND = -1;

/**
 * OPTIONAL behaviour seams folded onto the base `ApexWorldConfig` (kept here, in
 * apex.ts, so the shared `types.ts` interface stays untouched). EVERY field is
 * optional; supplying NONE makes the apex behave byte-identically to the literal
 * population-only / hand-set-cadence / no-protection model it shipped with. These
 * resolve open backlog seams (composite significance, ladder-driven migration
 * cadence, summon protection) without perturbing the apex-OFF/default-config hash.
 */
export interface ApexBehaviorConfig {
  /**
   * COMPOSITE SIGNIFICANCE. When supplied, returns a colony's full "ecosystem
   * actor" significance score (territory stage + hive/chamber count + leaderboard
   * rank + headcount, however the caller folds them) that gates colony-hunting in
   * place of raw population. ABSENT ⇒ significance is `populationAt` exactly as
   * today. A non-finite/negative return is treated as 0 (ignored).
   */
  readonly significanceOf?: (cid: number) => number;
  /**
   * COMPOSITE SIGNIFICANCE (granular). Extra significance ADDED to a colony's
   * population from its territory standing. ABSENT ⇒ contributes 0. Ignored when
   * `significanceOf` is supplied (that hook is the whole score).
   */
  readonly territoryWeightOf?: (cid: number) => number;
  /**
   * COMPOSITE SIGNIFICANCE (granular). Extra significance ADDED to a colony's
   * population from its hive size (node/chamber count). ABSENT ⇒ contributes 0.
   * Ignored when `significanceOf` is supplied.
   */
  readonly hiveWeightOf?: (cid: number) => number;
  /**
   * LADDER-DRIVEN MIGRATION CADENCE. The voluntary-migration interval, in ticks.
   * Takes precedence over `timescale`. ABSENT ⇒ derive from `timescale`, else fall
   * back to the literal `migrateInterval`.
   */
  readonly migrateIntervalOf?: () => number;
  /**
   * LADDER-DRIVEN MIGRATION CADENCE. The timescale ladder; when supplied (and no
   * `migrateIntervalOf`), the voluntary-migration interval is derived from
   * `layerPeriod(Evolution)`. ABSENT ⇒ the literal `migrateInterval` (byte-identical).
   */
  readonly timescale?: TimescaleConfig;
  /**
   * SUMMON PROTECTION. Ticks for which wildlife SUMMONED by this apex (Rat-King
   * rats) is EXEMPT from that apex's own hunting/devour. 0 (default) ⇒ no exemption,
   * byte-identical to today (the boss may eat its own reinforcements). >0 allocates
   * a single per-entity Int32Array (one-time, NOT in the hot path).
   */
  readonly protectSummonsTicks?: number;
}

/** `ApexWorldConfig` plus the optional behaviour seams above (apex.ts-local). */
type ApexWorldConfigExt = ApexWorldConfig & ApexBehaviorConfig;

export class ApexSystem implements System {
  readonly name = 'apex';

  private readonly entities: EntitySystem;
  private readonly grid: WorldGrid;
  private readonly pathing: GreedyPathing;
  private readonly colonies: ColonySystem;
  private readonly genes: GeneInventory;
  private readonly wildlife: WildlifeSystem;
  private readonly rng: Rng;
  private readonly config: ApexWorldConfigExt;
  /**
   * Mean config maxHp across the boss kinds — the reference the power-scaled hp pool is
   * distributed around (so each boss keeps its base-hp identity while the tier tracks the
   * player). Computed once from the live configs so it can never drift from them.
   */
  private readonly meanBaseHp: number;

  /**
   * Effective voluntary-migration interval (ticks). Resolved ONCE at construction:
   * `migrateIntervalOf()` → `layerPeriod(timescale, Evolution)` → the literal
   * `config.migrateInterval`. Static, so no per-tick recompute.
   */
  private readonly migrateInterval: number;

  /**
   * SUMMON PROTECTION: tick a wildlife entity was summoned by this apex, indexed by
   * EntityId; -1 = "not an apex summon". Allocated ONLY when `protectSummonsTicks>0`
   * (so the default path allocates nothing and is byte-identical). `protectSummonsTicks`
   * is cached as a non-negative scalar.
   */
  private readonly summonTick: Int32Array | null;
  private readonly protectSummonsTicks: number;

  // --- The single live boss (NIL/sentinels when none). ---
  /** Entity id of the live boss, or NIL when no boss is alive. */
  private liveId: EntityId = NIL;
  /** APEX_KIND.* of the live boss, or NO_KIND when none. */
  private liveKind = NO_KIND;
  /** Tick the live boss spawned (for ticks-alive + escape timing). */
  private spawnTick = 0;
  /** Deterministic rotation cursor over APEX_KIND (which boss spawns next). */
  private spawnCursor = 0;
  /**
   * Scaled max-hp of the LIVE boss (its config hp when no power source is wired). Set
   * on spawn, 0 when no boss lives. Drives the hp bar (`bossMaxHp`); folded into hash()
   * ONLY when power scaling is active, so the apex-off / unscaled hash is byte-identical.
   */
  private liveMaxHp = 0;
  /**
   * OPTIONAL player-power source (late-bound via `attachPowerSource`). When wired, a
   * boss spawns with hp banded to enemyScaling.APEX_BAND × the player colony's power
   * (clamped so it is never trivial nor an impossible wall) instead of its literal
   * config hp. Absent ⇒ config hp exactly, byte-identical to before.
   */
  private powerSource: (() => number) | null = null;

  // --- Territory + mode state machine (the ecosystem-force model). All scalars;
  //     reset on spawn; folded into hash(). ---
  /** Centre X of the boss's current territory. */
  private territoryX = 0;
  /** Centre Y of the boss's current territory. */
  private territoryY = 0;
  /** APEX_MODE.* of the live boss (Patrol when none/at spawn). */
  private mode: number = APEX_MODE.Patrol;
  /** Tick a post-kill Retreat ends (advance toward territory until then). */
  private retreatUntil = 0;
  /** Tick of the boss's last migration (voluntary cadence is measured off this). */
  private lastMigrateTick = 0;
  /** Consecutive-ish ticks spent within nestBuffer of a nest (anti-camp; decays). */
  private nestProximityTicks = 0;
  /** While tick < this, the boss avoids hunting toward / stepping onto nests (post-camp). */
  private nestAvoidUntil = 0;
  /** Tick the boss last took soldier damage (besieged ⇒ hold ground, no flee). */
  private lastDamagedTick = -1000;

  /** Damage dealt to the live boss, per colony (reset on each spawn). */
  private readonly damageByColony: Int32Array;

  // --- Alert ring buffer (boss sightings). Capped; oldest overwritten. ---
  private readonly alertKind: Int32Array;
  private readonly alertTick: Int32Array;
  private readonly alertX: Int32Array;
  private readonly alertY: Int32Array;
  private alertHead = 0; // next write slot
  private alertLen = 0; // valid entries [0, alertCap]
  private readonly alertCap: number;

  // --- Defeat-history ring buffer (the milestones). Capped. ---
  private readonly histKind: Int32Array;
  private readonly histTick: Int32Array;
  private readonly histVictor: Int32Array;
  private readonly histTicksAlive: Int32Array;
  private readonly histOutcome: Int32Array;
  private histHead = 0;
  private histLen = 0;
  private readonly histCap: number;

  // --- Public counters (deterministic; folded into the hash, read by tests). ---
  spawnedTotal = 0;
  defeatedTotal = 0;
  escapedTotal = 0;
  /** Rat-King reinforcements summoned across all bosses (ecosystem impact). */
  summonedTotal = 0;
  /** Wildlife devoured by the Pond Leviathan (ecosystem impact). */
  devouredTotal = 0;
  /** Colony organisms slain by bosses (ecosystem impact). */
  preyKills = 0;

  constructor(
    entities: EntitySystem,
    grid: WorldGrid,
    pathing: GreedyPathing,
    colonies: ColonySystem,
    genes: GeneInventory,
    wildlife: WildlifeSystem,
    rng: Rng,
    config: ApexWorldConfig,
  ) {
    this.entities = entities;
    this.grid = grid;
    this.pathing = pathing;
    this.colonies = colonies;
    this.genes = genes;
    this.wildlife = wildlife;
    this.rng = rng;
    this.config = config as ApexWorldConfigExt;

    // Mean boss hp across the kinds — the distribution reference for power-scaled hp.
    let hpSum = 0;
    const kinds = config.configs.length;
    for (let k = 0; k < kinds; k++) hpSum += (config.configs[k] as ApexConfig).maxHp;
    this.meanBaseHp = kinds > 0 ? Math.floor(hpSum / kinds) : 0;

    // LADDER-DRIVEN MIGRATION CADENCE: resolve the interval once. Precedence:
    // explicit hook → Evolution-layer period → the literal config.migrateInterval.
    const ext = this.config;
    if (ext.migrateIntervalOf !== undefined) {
      const v = ext.migrateIntervalOf();
      this.migrateInterval = v > 0 ? (v | 0) : config.migrateInterval;
    } else if (ext.timescale !== undefined) {
      const v = layerPeriod(ext.timescale, LAYER.Evolution);
      this.migrateInterval = v > 0 ? (v | 0) : config.migrateInterval;
    } else {
      this.migrateInterval = config.migrateInterval;
    }

    // SUMMON PROTECTION: cache a non-negative window; allocate the per-entity tick
    // column ONLY when actually protecting (default 0 ⇒ null ⇒ zero allocation and
    // a byte-identical sense/devour path).
    const psRaw = ext.protectSummonsTicks ?? 0;
    this.protectSummonsTicks = psRaw > 0 ? (psRaw | 0) : 0;
    this.summonTick = this.protectSummonsTicks > 0 ? new Int32Array(entities.capacity).fill(-1) : null;

    this.damageByColony = new Int32Array(colonies.capacity);

    this.alertCap = Math.max(1, config.alertCap);
    this.alertKind = new Int32Array(this.alertCap);
    this.alertTick = new Int32Array(this.alertCap);
    this.alertX = new Int32Array(this.alertCap);
    this.alertY = new Int32Array(this.alertCap);

    this.histCap = Math.max(1, config.historyCap);
    this.histKind = new Int32Array(this.histCap);
    this.histTick = new Int32Array(this.histCap);
    this.histVictor = new Int32Array(this.histCap);
    this.histTicksAlive = new Int32Array(this.histCap);
    this.histOutcome = new Int32Array(this.histCap);
  }

  // --- Tuning lookup ---------------------------------------------------------

  private cfgOf(kind: number): ApexConfig {
    return this.config.configs[kind] as ApexConfig;
  }

  /**
   * Late-bind the player-power source (mirrors EcosystemSystem.attachPowerSource).
   * Once wired, a boss spawns with a power-banded hp pool; not wiring it leaves bosses
   * at their literal config hp, byte-identical to before.
   */
  attachPowerSource(source: () => number): void {
    this.powerSource = source;
  }

  /**
   * COMPOSITE SIGNIFICANCE — a colony's "ecosystem actor" score for the hunt gate.
   * With NO behaviour hooks wired this returns `populationAt(col)` EXACTLY (so the
   * `score < significancePop` test is byte-identical to the population-only model).
   * With hooks: `significanceOf` (if present) is the whole score; otherwise the
   * granular `territoryWeightOf` + `hiveWeightOf` are ADDED to population. A
   * non-finite/negative contribution is clamped to 0 (never lowers significance,
   * keeps it integer + deterministic).
   */
  private significanceScore(col: number): number {
    const ext = this.config;
    if (ext.significanceOf !== undefined) {
      return this.nonNeg(ext.significanceOf(col));
    }
    let score = this.colonies.populationAt(col);
    if (ext.territoryWeightOf !== undefined) score += this.nonNeg(ext.territoryWeightOf(col));
    if (ext.hiveWeightOf !== undefined) score += this.nonNeg(ext.hiveWeightOf(col));
    return score;
  }

  /** Clamp a hook's contribution to a non-negative integer (NaN/neg ⇒ 0). */
  private nonNeg(v: number): number {
    return v > 0 ? (v | 0) : 0;
  }

  /**
   * SUMMON PROTECTION test: is entity `c` an apex summon still inside its protection
   * window at `tick`? `prot[c] >= 0` marks a summon; the window is exclusive
   * (`tick - prot[c] < protTicks`) so a kill becomes possible exactly `protTicks`
   * ticks after the summon. Pure integer, no allocation.
   */
  private isProtectedSummon(prot: Int32Array, protTicks: number, c: number, tick: Tick): boolean {
    if (c < 0 || c >= prot.length) return false;
    const st = prot[c] as number;
    return st >= 0 && tick - st < protTicks;
  }

  /** True when ANY composite-significance hook is wired (else population-only fast path). */
  private hasSignificanceHooks(): boolean {
    const ext = this.config;
    return (
      ext.significanceOf !== undefined ||
      ext.territoryWeightOf !== undefined ||
      ext.hiveWeightOf !== undefined
    );
  }

  // --- Lifecycle -------------------------------------------------------------

  /**
   * Spawn a boss of `kind` at/near a scored TERRITORY centre (nest-avoiding), not
   * a purely random tile. Returns the boss entity id, or NIL if no passable tile
   * turned up. Resets the per-colony damage ledger + the mode state machine and
   * pushes an alert. A boss does not recycle the wildlife per-species columns — it
   * stands alone (single-boss).
   */
  spawnApex(kind: ApexCreature, tick: Tick): EntityId {
    if (this.liveId !== NIL) return NIL; // one boss at a time
    // Pick a territory first (scored, nest-avoiding); fall back to any passable
    // tile only if scoring turned up nothing (tiny/walled maps).
    const terr = this.pickTerritory(tick, 0);
    const tile = terr !== NIL ? terr : this.pickPassable();
    if (tile === NIL) return NIL;
    const tcx = tile % this.grid.width;
    const tcy = (tile / this.grid.width) | 0;
    // Spawn AT the territory centre when passable, else a passable tile near it.
    let spawnTile = tile;
    if (!this.grid.isPassable(tcx, tcy)) {
      spawnTile = this.pickPassableNear(tcx, tcy, 2);
      if (spawnTile === NIL) spawnTile = this.pickPassable();
      if (spawnTile === NIL) return NIL;
    }
    const x = spawnTile % this.grid.width;
    const y = (spawnTile / this.grid.width) | 0;
    const cfg = this.cfgOf(kind);

    const e = this.entities;
    const id = e.spawn(KIND.Apex, x, y);
    // POWER-TARGETED hp: band the siege pool to the player colony's strength when a
    // power source is wired (drawing one apex-RNG int for variance); otherwise the
    // literal config hp (no draw ⇒ byte-identical spawn stream). Recorded as liveMaxHp
    // so the hp bar reads the scaled pool.
    const maxHp =
      this.powerSource !== null
        ? apexScaledHp(cfg.maxHp, this.meanBaseHp, this.powerSource(), this.rng)
        : cfg.maxHp;
    e.hp[id] = maxHp;
    this.liveMaxHp = maxHp;
    e.state[id] = 0;
    e.targetX[id] = x;
    e.targetY[id] = y;
    e.colony[id] = NIL; // a boss belongs to no colony
    // Flyers don't occupy a tile (don't count toward the per-tile cap).
    e.blocks[id] = cfg.flying ? 0 : 1;
    this.grid.add(id);

    this.liveId = id;
    this.liveKind = kind;
    this.spawnTick = tick;
    this.damageByColony.fill(0);
    // SUMMON PROTECTION: clear stale stamps so a new boss never inherits the prior
    // boss's protected summons (no-op when protection is disabled — column is null).
    if (this.summonTick !== null) this.summonTick.fill(-1);

    // --- Reset the territory + mode state machine. ---
    this.territoryX = tcx;
    this.territoryY = tcy;
    this.mode = APEX_MODE.Patrol;
    this.retreatUntil = 0;
    this.lastMigrateTick = tick;
    this.nestProximityTicks = 0;
    this.nestAvoidUntil = 0;
    this.lastDamagedTick = -1000;

    this.pushAlert(kind, tick, x, y);
    this.spawnedTotal++;
    return id;
  }

  /** Remove the live boss from the grid + entity store and clear live state. */
  private clearBoss(): void {
    if (this.liveId === NIL) return;
    this.grid.remove(this.liveId);
    this.entities.despawn(this.liveId);
    this.liveId = NIL;
    this.liveKind = NO_KIND;
    this.liveMaxHp = 0;
  }

  // --- Per-tick --------------------------------------------------------------

  update(tick: Tick): void {
    // 1) Rare spawn — only when no boss lives, on the cadence, on a low roll.
    if (this.liveId === NIL) {
      if (tick % this.config.spawnCheckInterval === 0 && this.rng.int(100) < this.config.spawnChancePct) {
        const kind = (this.spawnCursor % APEX_KIND_COUNT) as ApexCreature;
        this.spawnCursor = (this.spawnCursor + 1) % APEX_KIND_COUNT;
        this.spawnApex(kind, tick);
      }
      return; // nothing else to do this tick (no live boss)
    }

    const id = this.liveId;
    const kind = this.liveKind;
    const cfg = this.cfgOf(kind);

    // 2) Boss behaviour (unique per kind).
    this.behave(id, kind, cfg, tick);
    if (this.liveId === NIL) return; // (defensive) behaviour cleared it

    // 3) Colonies fight back: Soldiers in range strip hp + accrue attribution.
    this.applyColonyDamage(id, cfg, tick);

    // 4) Resolve: defeat (hp<=0) → loot + history; else escape on lifespan.
    if ((this.entities.hp[id] as number) <= 0) {
      this.resolveDefeat(kind, tick);
    } else if (tick - this.spawnTick >= cfg.maxLifespan) {
      this.resolveEscape(kind, tick);
    }
    // Energy/upkeep intentionally skipped: a boss never starves (drama). Its only
    // clock is maxLifespan.
  }

  // --- Behaviour: the territory + mode state machine -------------------------
  // Bounded + allocation-free. The shared movement core (anti-camp accounting,
  // forced/voluntary migration, retreat, migrate, hunt, patrol) runs for EVERY
  // kind; the kind-specific UPKEEP (Rat-King summons, Leviathan devour) layers on
  // top as ecosystem impact.

  private behave(id: EntityId, kind: number, cfg: ApexConfig, tick: Tick): void {
    const besieged = tick - this.lastDamagedTick <= 2;

    // 1) Anti-camp accounting + the HARD forced-migration guarantee.
    this.accountNestProximity(id, cfg, tick);

    // 2) Voluntary migration on cadence (skipped while besieged or mid-Retreat or
    //    if a forced migration this tick already set Migrate).
    if (
      !besieged &&
      this.mode !== APEX_MODE.Retreat &&
      this.mode !== APEX_MODE.Migrate &&
      tick - this.lastMigrateTick >= this.migrateInterval
    ) {
      const t = this.pickTerritory(tick, 0);
      if (t !== NIL) {
        this.territoryX = t % this.grid.width;
        this.territoryY = (t / this.grid.width) | 0;
        this.mode = APEX_MODE.Migrate;
        this.lastMigrateTick = tick;
      }
    }

    // 3) Retreat / Migrate are committed movements toward the territory centre.
    if (this.mode === APEX_MODE.Retreat) {
      if (tick < this.retreatUntil) {
        this.advance(id, this.territoryX, this.territoryY, cfg);
        this.kindUpkeep(id, kind, cfg, tick);
        return;
      }
      this.mode = APEX_MODE.Patrol; // retreat expired
    }
    if (this.mode === APEX_MODE.Migrate) {
      const ax = this.entities.posX[id] as number;
      const ay = this.entities.posY[id] as number;
      if (chebyshev(ax, ay, this.territoryX, this.territoryY) <= 1) {
        this.mode = APEX_MODE.Patrol; // arrived
      } else {
        this.advance(id, this.territoryX, this.territoryY, cfg);
        this.kindUpkeep(id, kind, cfg, tick);
        return;
      }
    }

    // 4) Hunt (eligible target in range) → else Patrol/roam. Both honour the
    //    no-step-onto-a-nest guard while avoiding nests; a successful non-besieged
    //    kill enters Retreat (the anti chain-wipe rule).
    this.huntOrPatrol(id, cfg, tick, besieged);

    // 5) Kind-specific ecosystem upkeep.
    this.kindUpkeep(id, kind, cfg, tick);
  }

  /** Kind-specific ecosystem impact (Rat-King summons, Leviathan devour). */
  private kindUpkeep(id: EntityId, kind: number, cfg: ApexConfig, tick: Tick): void {
    if (this.liveId === NIL) return; // a kill in huntOrPatrol cleared nothing, but be safe
    switch (kind) {
      case APEX_KIND.RatKing:
        if (cfg.summonInterval > 0 && (tick - this.spawnTick) % cfg.summonInterval === 0) {
          this.summonRat(id, tick);
        }
        break;
      case APEX_KIND.PondLeviathan:
        // Area-denial: every few ticks, devour the nearest wildlife in a small
        // radius (disrupts the ecosystem around the Leviathan).
        if ((tick - this.spawnTick) % 3 === 0) this.devourWildlife(id, cfg, tick);
        break;
      default:
        break;
    }
  }

  /**
   * Anti-camp accounting + the HARD forced-migration guarantee. If the boss is
   * within nestBuffer (Chebyshev) of the nearest nest, accrue proximity; else
   * decay it toward 0. On reaching maxNestProximityTicks, FORCE a migration to a
   * strongly-nest-avoiding territory and arm the nest-avoid window — this is what
   * guarantees an apex cannot remain near a player nest indefinitely.
   */
  private accountNestProximity(id: EntityId, _cfg: ApexConfig, tick: Tick): void {
    const e = this.entities;
    const ax = e.posX[id] as number;
    const ay = e.posY[id] as number;
    const nd = this.nearestNestDist(ax, ay);
    if (nd >= 0 && nd <= this.config.nestBuffer) {
      this.nestProximityTicks++;
    } else if (this.nestProximityTicks > 0) {
      this.nestProximityTicks--;
    }
    if (this.nestProximityTicks >= this.config.maxNestProximityTicks) {
      const t = this.pickTerritory(tick, 1); // strong nest-avoidance
      if (t !== NIL) {
        this.territoryX = t % this.grid.width;
        this.territoryY = (t / this.grid.width) | 0;
      }
      this.nestAvoidUntil = tick + this.config.nestAvoidTicks;
      this.mode = APEX_MODE.Migrate;
      this.lastMigrateTick = tick;
      this.nestProximityTicks = 0;
    }
  }

  /**
   * Hunt the nearest ELIGIBLE target within senseRadius, else patrol. Wildlife is
   * always eligible (apex = ecosystem predator); a colony organism is eligible
   * only if its colony is a SIGNIFICANT actor (populationAt ≥ significancePop),
   * the nest-avoid window has lapsed, and the target is within territoryRadius of
   * the territory centre. Adjacent ⇒ strike; else advance. A successful colony
   * kill (when not besieged) triggers Retreat.
   */
  private huntOrPatrol(id: EntityId, cfg: ApexConfig, tick: Tick, besieged: boolean): void {
    const e = this.entities;
    const ax = e.posX[id] as number;
    const ay = e.posY[id] as number;
    const target = this.senseTarget(ax, ay, cfg.senseRadius, tick);
    if (target !== NIL) {
      this.mode = APEX_MODE.Hunt;
      const px = e.posX[target] as number;
      const py = e.posY[target] as number;
      if (chebyshev(ax, ay, px, py) <= 1) {
        const wasWildlife = (e.kind[target] as number) === KIND.Wildlife;
        const killed = this.strikeTarget(target, cfg);
        // Retreat only after a successful COLONY kill (the nest-chain-wipe brake);
        // wildlife predation does not trigger a withdrawal.
        if (killed && !wasWildlife && !besieged) {
          this.mode = APEX_MODE.Retreat;
          this.retreatUntil = tick + this.config.retreatTicks;
        }
      } else {
        this.advanceHunt(id, px, py, cfg, tick);
      }
      return;
    }
    // No eligible target → Patrol. A BESIEGED boss HOLDS GROUND (a cornered apex
    // fights — it does not wander out of a soldier swarm, preserving the defeat
    // economy). Otherwise pull back inside the territory if beyond it, else roam.
    this.mode = APEX_MODE.Patrol;
    if (besieged) return; // hold position under attack
    if (chebyshev(ax, ay, this.territoryX, this.territoryY) > this.config.territoryRadius) {
      this.advance(id, this.territoryX, this.territoryY, cfg);
    } else {
      this.drift(id, cfg);
    }
  }

  /**
   * Strike an eligible target. WILDLIFE → the wildlife layer owns the kill
   * (killWildlife) + a deterministic devour counter. A COLONY ORGANISM → subtract
   * attackPower; on hp<=0 fix the colony census (onDeath) then remove + despawn it
   * (the world's preyKill chokepoint, bumping preyKills). Returns whether the
   * target died this strike.
   */
  private strikeTarget(targetId: EntityId, cfg: ApexConfig): boolean {
    const e = this.entities;
    if ((e.kind[targetId] as number) === KIND.Wildlife) {
      if (this.summonTick !== null && targetId >= 0 && targetId < this.summonTick.length) {
        this.summonTick[targetId] = -1; // free the stamp before the id can recycle
      }
      this.wildlife.killWildlife(targetId);
      this.devouredTotal++;
      return true;
    }
    const hp = (e.hp[targetId] as number) - cfg.attackPower;
    e.hp[targetId] = hp;
    if (hp <= 0) {
      this.colonies.onDeath(e.colony[targetId] as ColonyId, e.caste[targetId] as Caste);
      this.grid.remove(targetId);
      e.despawn(targetId);
      this.preyKills++;
      return true;
    }
    return false;
  }

  /** Rat King: spawn a rat reinforcement near the boss (graceful on exhaustion). */
  private summonRat(id: EntityId, tick: Tick): void {
    const e = this.entities;
    // Guard against entity-capacity exhaustion: the shared store throws when full,
    // so only summon when there's headroom (single boss + a few summons fit the
    // builder's reserved capacity, but stay defensive).
    if (e.aliveCount >= e.capacity) return;
    const ax = e.posX[id] as number;
    const ay = e.posY[id] as number;
    const tile = this.pickPassableNear(ax, ay, 3);
    if (tile === NIL) return;
    const sx = tile % this.grid.width;
    const sy = (tile / this.grid.width) | 0;
    const rid = this.wildlife.spawnWildlife(SPECIES.Rat, sx, sy);
    // SUMMON PROTECTION: stamp the summon tick on the freshly-pooled entity id so
    // this apex won't immediately eat its own reinforcement (only when enabled).
    if (this.summonTick !== null && rid >= 0 && rid < this.summonTick.length) {
      this.summonTick[rid] = tick;
    }
    this.summonedTotal++;
  }

  /** Pond Leviathan: devour the nearest wildlife within a small radius. */
  private devourWildlife(id: EntityId, cfg: ApexConfig, tick: Tick): void {
    const e = this.entities;
    const ax = e.posX[id] as number;
    const ay = e.posY[id] as number;
    const r = cfg.senseRadius < 4 ? cfg.senseRadius : 4;
    const victim = this.senseWildlife(ax, ay, r, tick);
    if (victim === NIL) return;
    if (this.summonTick !== null && victim >= 0 && victim < this.summonTick.length) {
      this.summonTick[victim] = -1; // free the stamp before the id can recycle
    }
    this.wildlife.killWildlife(victim);
    this.devouredTotal++;
  }

  // --- Colony fights back ----------------------------------------------------

  /**
   * Scan tiles within cfg.vulnRadius of the boss for live colony Soldiers. Each
   * such Soldier strips config.soldierDamage from the boss AND adds it to that
   * colony's damage ledger (defeat attribution). Bounded box scan, no allocation.
   * Records lastDamagedTick when it deals >0 damage so the next tick's behaviour
   * knows the boss is BESIEGED (hold ground — do not flee a soldier swarm).
   */
  private applyColonyDamage(id: EntityId, cfg: ApexConfig, tick: Tick): void {
    const e = this.entities;
    const grid = this.grid;
    const ax = e.posX[id] as number;
    const ay = e.posY[id] as number;
    const r = cfg.vulnRadius;
    const dmg = this.config.soldierDamage;

    const minX = ax - r < 0 ? 0 : ax - r;
    const minY = ay - r < 0 ? 0 : ay - r;
    const maxX = ax + r >= grid.width ? grid.width - 1 : ax + r;
    const maxY = ay + r >= grid.height ? grid.height - 1 : ay + r;

    let total = 0;
    for (let ty = minY; ty <= maxY; ty++) {
      for (let tx = minX; tx <= maxX; tx++) {
        const ti = grid.index(tx, ty);
        for (let cur = grid.headAt(ti); cur !== NIL; cur = grid.next(cur)) {
          const c = cur as number;
          if ((e.kind[c] as number) !== KIND.Organism) continue;
          if ((e.caste[c] as number) !== CASTE.Soldier) continue;
          if ((e.hp[c] as number) <= 0) continue;
          const col = e.colony[c] as number;
          if (col < 0 || col >= this.damageByColony.length) continue;
          this.damageByColony[col] = (this.damageByColony[col] as number) + dmg;
          total += dmg;
        }
      }
    }
    if (total > 0) {
      e.hp[id] = (e.hp[id] as number) - total;
      this.lastDamagedTick = tick;
    }
  }

  // --- Resolution ------------------------------------------------------------

  /**
   * Defeat: the colony with the most accrued damage (ties → lowest id) loots a
   * unique gene; record the milestone; clear the boss. If NOBODY dealt damage
   * (e.g. it starved to a wall — impossible here — or was cleared otherwise) no
   * victor is recorded and no gene drops.
   */
  private resolveDefeat(kind: number, tick: Tick): void {
    const victor = this.argmaxDamage();
    if (victor !== NIL) {
      awardGeneFromEvent(this.genes, this.rng, victor, tick, this.cfgOf(kind).geneQualityBonus);
    }
    this.pushHistory(kind, tick, victor, tick - this.spawnTick, APEX_OUTCOME.Defeated);
    this.defeatedTotal++;
    this.clearBoss();
  }

  /** Escape: the boss survived its lifespan undefeated. Record + clear. */
  private resolveEscape(kind: number, tick: Tick): void {
    this.pushHistory(kind, tick, NIL, tick - this.spawnTick, APEX_OUTCOME.Escaped);
    this.escapedTotal++;
    this.clearBoss();
  }

  /** Colony id with the most accrued damage (ties → lowest id), or NIL if none. */
  private argmaxDamage(): ColonyId {
    let best: ColonyId = NIL;
    let bestDmg = 0;
    for (let cid = 0; cid < this.damageByColony.length; cid++) {
      const d = this.damageByColony[cid] as number;
      if (d > bestDmg) {
        bestDmg = d;
        best = cid;
      }
    }
    return best;
  }

  // --- Movement helpers (mirror wildlife.stepOnce / advance / wander) ---------

  /**
   * One greedy step toward (tx,ty). Ground respects walls + the per-tile cap (via
   * pathing); flying is bounds-only (ignores walls/crowds). Returns whether moved.
   */
  private stepOnce(id: EntityId, tx: number, ty: number, flying: boolean): boolean {
    const e = this.entities;
    const ax = e.posX[id] as number;
    const ay = e.posY[id] as number;
    if (ax === tx && ay === ty) return false;
    if (!flying) {
      const dir = this.pathing.step(ax, ay, tx, ty);
      if (dir === DIR.Stay) return false;
      this.grid.move(id, ax + (DIR_DX[dir] as number), ay + (DIR_DY[dir] as number));
      return true;
    }
    let bestDir: number = DIR.Stay;
    let bestDist = chebyshev(ax, ay, tx, ty);
    for (let d = 0; d < DIR_COUNT; d++) {
      const nx = ax + (DIR_DX[d] as number);
      const ny = ay + (DIR_DY[d] as number);
      if (!this.grid.inBounds(nx, ny)) continue;
      const dist = chebyshev(nx, ny, tx, ty);
      if (dist < bestDist) {
        bestDist = dist;
        bestDir = d;
      }
    }
    if (bestDir === DIR.Stay) return false;
    this.grid.move(id, ax + (DIR_DX[bestDir] as number), ay + (DIR_DY[bestDir] as number));
    return true;
  }

  /** Advance up to cfg.stepsPerTick steps toward (tx,ty). Stops on arrival/block. */
  private advance(id: EntityId, tx: number, ty: number, cfg: ApexConfig): void {
    const e = this.entities;
    for (let s = 0; s < cfg.stepsPerTick; s++) {
      if (!this.stepOnce(id, tx, ty, cfg.flying)) break;
      if ((e.posX[id] as number) === tx && (e.posY[id] as number) === ty) break;
    }
  }

  /**
   * Advance toward a hunt target, but while the nest-avoid window is active
   * (tick < nestAvoidUntil) DO NOT step within nestBuffer of any nest — the boss
   * pursues prey but will not be drawn back onto a nest it was just forced off.
   */
  private advanceHunt(id: EntityId, tx: number, ty: number, cfg: ApexConfig, tick: Tick): void {
    if (tick >= this.nestAvoidUntil) {
      this.advance(id, tx, ty, cfg);
      return;
    }
    const e = this.entities;
    for (let s = 0; s < cfg.stepsPerTick; s++) {
      const ax = e.posX[id] as number;
      const ay = e.posY[id] as number;
      if (ax === tx && ay === ty) break;
      // Peek the would-be step; abort the whole advance if it lands inside the
      // nest buffer (deterministic: a single greedy peek, no re-search).
      const peek = this.peekStep(ax, ay, tx, ty, cfg.flying);
      if (peek < 0) break;
      const nx = ax + (DIR_DX[peek] as number);
      const ny = ay + (DIR_DY[peek] as number);
      const nd = this.nearestNestDist(nx, ny);
      if (nd >= 0 && nd <= this.config.nestBuffer) break; // would camp a nest → stop
      this.grid.move(id, nx, ny);
    }
  }

  /**
   * The direction one greedy step would take from (ax,ay) toward (tx,ty), or -1 if
   * none makes progress. Ground uses pathing (walls + cap); flying is bounds-only.
   * Pure: it does NOT move the entity (used to peek before a nest-buffer guard).
   */
  private peekStep(ax: number, ay: number, tx: number, ty: number, flying: boolean): number {
    if (ax === tx && ay === ty) return -1;
    if (!flying) {
      const dir = this.pathing.step(ax, ay, tx, ty);
      return dir === DIR.Stay ? -1 : dir;
    }
    let bestDir = -1;
    let bestDist = chebyshev(ax, ay, tx, ty);
    for (let d = 0; d < DIR_COUNT; d++) {
      const nx = ax + (DIR_DX[d] as number);
      const ny = ay + (DIR_DY[d] as number);
      if (!this.grid.inBounds(nx, ny)) continue;
      const dist = chebyshev(nx, ny, tx, ty);
      if (dist < bestDist) {
        bestDist = dist;
        bestDir = d;
      }
    }
    return bestDir;
  }

  /** One deterministic random step (drift). Honours flight. */
  private drift(id: EntityId, cfg: ApexConfig): void {
    const e = this.entities;
    const grid = this.grid;
    const ax = e.posX[id] as number;
    const ay = e.posY[id] as number;
    const start = this.rng.int(DIR_COUNT);
    for (let k = 0; k < DIR_COUNT; k++) {
      const d = (start + k) % DIR_COUNT;
      const nx = ax + (DIR_DX[d] as number);
      const ny = ay + (DIR_DY[d] as number);
      const ok = cfg.flying ? grid.inBounds(nx, ny) : grid.canMoveAnt(nx, ny);
      if (ok) {
        grid.move(id, nx, ny);
        return;
      }
    }
  }

  // --- Sensing (bounded Chebyshev box scans, (y,x) order, ties → lower id) ----

  /**
   * Nearest ELIGIBLE target within radius r (ties → lower id), or NIL. Eligibility:
   *   - WILDLIFE (KIND.Wildlife, hp>0) is ALWAYS eligible (ecosystem predator).
   *   - A COLONY ORGANISM (KIND.Organism, hp>0) only when its colony is SIGNIFICANT
   *     (populationAt ≥ significancePop), the nest-avoid window has lapsed
   *     (tick ≥ nestAvoidUntil), AND it is within territoryRadius of the territory
   *     centre (no map-wide chase / chase into an avoided nest).
   * One unified box scan (no allocation); a single best wins across both kinds.
   */
  private senseTarget(ax: number, ay: number, r: number, tick: Tick): EntityId {
    const e = this.entities;
    const grid = this.grid;
    let bestId: EntityId = NIL;
    let bestDist = r + 1;
    let bestTieId = 0x7fffffff;
    const colonyEligible = tick >= this.nestAvoidUntil;
    const sig = this.config.significancePop;
    const tRad = this.config.territoryRadius;
    // COMPOSITE SIGNIFICANCE: only route through the score helper when a hook is
    // wired; otherwise keep the literal populationAt comparison (byte-identical).
    const composite = this.hasSignificanceHooks();
    // SUMMON PROTECTION: hoist the window + column (null when disabled ⇒ no checks).
    const prot = this.summonTick;
    const protTicks = this.protectSummonsTicks;

    const minX = ax - r < 0 ? 0 : ax - r;
    const minY = ay - r < 0 ? 0 : ay - r;
    const maxX = ax + r >= grid.width ? grid.width - 1 : ax + r;
    const maxY = ay + r >= grid.height ? grid.height - 1 : ay + r;

    for (let ty = minY; ty <= maxY; ty++) {
      for (let tx = minX; tx <= maxX; tx++) {
        const ti = grid.index(tx, ty);
        for (let cur = grid.headAt(ti); cur !== NIL; cur = grid.next(cur)) {
          const c = cur as number;
          const k = e.kind[c] as number;
          if (k !== KIND.Wildlife && k !== KIND.Organism) continue;
          if ((e.hp[c] as number) <= 0) continue;
          if (k === KIND.Wildlife) {
            // SUMMON PROTECTION: skip very-recently-summoned reinforcements.
            if (prot !== null && this.isProtectedSummon(prot, protTicks, c, tick)) continue;
          } else {
            // Threat escalation + territory + nest-avoid gating.
            if (!colonyEligible) continue;
            const col = e.colony[c] as number;
            if (col < 0) continue;
            const sigScore = composite ? this.significanceScore(col) : this.colonies.populationAt(col);
            if (sigScore < sig) continue;
            if (chebyshev(tx, ty, this.territoryX, this.territoryY) > tRad) continue;
          }
          const dist = chebyshev(ax, ay, tx, ty);
          if (dist < bestDist || (dist === bestDist && c < bestTieId)) {
            bestDist = dist;
            bestId = cur;
            bestTieId = c;
          }
        }
      }
    }
    return bestId;
  }

  /**
   * Chebyshev distance from (x,y) to the NEAREST live colony nest, or -1 if there
   * are no colonies. Bounded loop over the (few) colonies — no allocation.
   */
  private nearestNestDist(x: number, y: number): number {
    const cols = this.colonies;
    const n = cols.count;
    let best = -1;
    for (let cid = 0; cid < n; cid++) {
      const d = chebyshev(x, y, cols.nestX(cid), cols.nestY(cid));
      if (best < 0 || d < best) best = d;
    }
    return best;
  }

  // --- Territory selection (scored, nest-avoiding, deterministic RNG sampling) -

  /**
   * Pick a new TERRITORY: sample territorySamples deterministic passable candidate
   * tiles, HARD-reject any within the nest buffer (widened when `avoidStrength`>0),
   * score the rest by a bounded box scan, and return the best tile index (ties →
   * lower tile index). Returns NIL if no candidate survived rejection (caller falls
   * back). Allocation-free: scalars only.
   */
  private pickTerritory(tick: Tick, avoidStrength: number): number {
    const grid = this.grid;
    const samples = this.config.territorySamples;
    // Post-camp, widen the nest rejection so the new territory is genuinely clear.
    const buffer = this.config.nestBuffer + (avoidStrength > 0 ? this.config.scoreRadius : 0);
    let bestTile = NIL;
    let bestScore = -0x7fffffff;
    for (let s = 0; s < samples; s++) {
      const x = this.rng.int(grid.width);
      const y = this.rng.int(grid.height);
      if (!grid.isPassable(x, y)) continue;
      const nd = this.nearestNestDist(x, y);
      if (nd >= 0 && nd <= buffer) continue; // never sit on / beside a nest
      const score = this.scoreTerritory(x, y);
      const tile = grid.index(x, y);
      if (score > bestScore || (score === bestScore && (bestTile === NIL || tile < bestTile))) {
        bestScore = score;
        bestTile = tile;
      }
    }
    // The tick parameter keeps the signature uniform with the caller's intent
    // (forced vs voluntary); selection itself is tick-independent (RNG-driven).
    void tick;
    return bestTile;
  }

  /**
   * Score a candidate territory centre by a bounded box scan (radius scoreRadius):
   *   + foodWeight     · nearby Food nodes (rich food zones)
   *   + wildlifeWeight · nearby Wildlife (prey concentrations)
   *   + contestedWeight· contested signal (organisms from ≥2 colonies nearby, OR
   *                      >1 nest near — but not ON one)
   *   − nestPenalty    · proximity-to-nearest-nest term (closer ⇒ larger penalty)
   * Integer + deterministic; (y,x) scan order; no allocation.
   */
  private scoreTerritory(cx: number, cy: number): number {
    const e = this.entities;
    const grid = this.grid;
    const r = this.config.scoreRadius;
    const minX = cx - r < 0 ? 0 : cx - r;
    const minY = cy - r < 0 ? 0 : cy - r;
    const maxX = cx + r >= grid.width ? grid.width - 1 : cx + r;
    const maxY = cy + r >= grid.height ? grid.height - 1 : cy + r;

    let food = 0;
    let wild = 0;
    // Distinct-colony detection over the (few) colonies via a small bitmask.
    let colonyMask = 0;
    let colonyKinds = 0;
    for (let ty = minY; ty <= maxY; ty++) {
      for (let tx = minX; tx <= maxX; tx++) {
        const ti = grid.index(tx, ty);
        for (let cur = grid.headAt(ti); cur !== NIL; cur = grid.next(cur)) {
          const c = cur as number;
          const k = e.kind[c] as number;
          if (k === KIND.Food) {
            food++;
          } else if (k === KIND.Wildlife) {
            if ((e.hp[c] as number) > 0) wild++;
          } else if (k === KIND.Organism) {
            if ((e.hp[c] as number) <= 0) continue;
            const col = e.colony[c] as number;
            if (col >= 0 && col < 31) {
              const bit = 1 << col;
              if ((colonyMask & bit) === 0) {
                colonyMask |= bit;
                colonyKinds++;
              }
            }
          }
        }
      }
    }

    // Contested: ≥2 distinct colonies' organisms nearby, OR >1 nest within range
    // (but the candidate is already guaranteed NOT on a nest by pickTerritory).
    let nestsNear = 0;
    const cols = this.colonies;
    for (let cid = 0; cid < cols.count; cid++) {
      if (chebyshev(cx, cy, cols.nestX(cid), cols.nestY(cid)) <= r) nestsNear++;
    }
    const contested = colonyKinds >= 2 || nestsNear > 1 ? 1 : 0;

    // Nest proximity penalty: larger the closer the nearest nest is to the buffer.
    const nd = this.nearestNestDist(cx, cy);
    let nestProx = 0;
    if (nd >= 0) {
      const within = this.config.nestBuffer + 1 - nd;
      if (within > 0) nestProx = within;
    }

    return (
      this.config.foodWeight * food +
      this.config.wildlifeWeight * wild +
      this.config.contestedWeight * contested -
      this.config.nestPenalty * nestProx
    );
  }

  /**
   * Nearest live wildlife (KIND.Wildlife, hp>0) within radius r, or NIL. Skips
   * wildlife inside this apex's SUMMON-PROTECTION window when one is configured
   * (byte-identical when protection is disabled — `prot` is null, no checks run).
   */
  private senseWildlife(ax: number, ay: number, r: number, tick: Tick): EntityId {
    const e = this.entities;
    const grid = this.grid;
    let bestId: EntityId = NIL;
    let bestDist = r + 1;
    let bestTieId = 0x7fffffff;
    const prot = this.summonTick;
    const protTicks = this.protectSummonsTicks;

    const minX = ax - r < 0 ? 0 : ax - r;
    const minY = ay - r < 0 ? 0 : ay - r;
    const maxX = ax + r >= grid.width ? grid.width - 1 : ax + r;
    const maxY = ay + r >= grid.height ? grid.height - 1 : ay + r;

    for (let ty = minY; ty <= maxY; ty++) {
      for (let tx = minX; tx <= maxX; tx++) {
        const ti = grid.index(tx, ty);
        for (let cur = grid.headAt(ti); cur !== NIL; cur = grid.next(cur)) {
          const c = cur as number;
          if ((e.kind[c] as number) !== KIND.Wildlife) continue;
          if ((e.hp[c] as number) <= 0) continue;
          if (prot !== null && this.isProtectedSummon(prot, protTicks, c, tick)) continue;
          const dist = chebyshev(ax, ay, tx, ty);
          if (dist < bestDist || (dist === bestDist && c < bestTieId)) {
            bestDist = dist;
            bestId = cur;
            bestTieId = c;
          }
        }
      }
    }
    return bestId;
  }

  // --- Passable-tile pickers (deterministic, RNG-driven, bounded) -------------

  private pickPassable(): number {
    const grid = this.grid;
    let tries = 0;
    let x = 0;
    let y = 0;
    do {
      x = this.rng.int(grid.width);
      y = this.rng.int(grid.height);
      tries++;
    } while (!grid.isPassable(x, y) && tries < 64);
    if (!grid.isPassable(x, y)) return NIL;
    return grid.index(x, y);
  }

  /** A passable tile within Chebyshev radius `r` of (cx,cy), or NIL. */
  private pickPassableNear(cx: number, cy: number, r: number): number {
    const grid = this.grid;
    const span = 2 * r + 1;
    for (let tries = 0; tries < 32; tries++) {
      const x = cx - r + this.rng.int(span);
      const y = cy - r + this.rng.int(span);
      if (grid.inBounds(x, y) && grid.isPassable(x, y)) return grid.index(x, y);
    }
    return NIL;
  }

  // --- Ring-buffer writers ---------------------------------------------------

  private pushAlert(kind: number, tick: Tick, x: number, y: number): void {
    const i = this.alertHead;
    this.alertKind[i] = kind;
    this.alertTick[i] = tick;
    this.alertX[i] = x;
    this.alertY[i] = y;
    this.alertHead = (i + 1) % this.alertCap;
    if (this.alertLen < this.alertCap) this.alertLen++;
  }

  private pushHistory(
    kind: number,
    tick: Tick,
    victor: number,
    ticksAlive: number,
    outcome: number,
  ): void {
    const i = this.histHead;
    this.histKind[i] = kind;
    this.histTick[i] = tick;
    this.histVictor[i] = victor;
    this.histTicksAlive[i] = ticksAlive;
    this.histOutcome[i] = outcome;
    this.histHead = (i + 1) % this.histCap;
    if (this.histLen < this.histCap) this.histLen++;
  }

  // --- Live-boss accessors ---------------------------------------------------

  /** True while a boss is alive. */
  bossAlive(): boolean {
    return this.liveId !== NIL;
  }
  /** APEX_KIND.* of the live boss, or -1 when none. */
  bossKind(): number {
    return this.liveKind;
  }

  // --- Resolved-behaviour accessors (observability for renderers + tests) -----

  /**
   * The EFFECTIVE voluntary-migration interval in ticks (after resolving the
   * optional `migrateIntervalOf` / `timescale` seams). Equals the literal
   * `config.migrateInterval` when neither is supplied.
   */
  bossMigrateInterval(): number {
    return this.migrateInterval;
  }
  /** The configured summon-protection window in ticks (0 when disabled / default). */
  summonProtectTicks(): number {
    return this.protectSummonsTicks;
  }
  /**
   * Tick a wildlife entity was summoned by this apex, or -1 if not a (still-tracked)
   * apex summon / protection disabled. Test + debug seam only.
   */
  summonTickAt(id: EntityId): number {
    if (this.summonTick === null || id < 0 || id >= this.summonTick.length) return -1;
    return this.summonTick[id] as number;
  }
  /**
   * Composite "ecosystem actor" significance score the hunt gate uses for colony
   * `cid` (population plus any wired territory/hive/leaderboard signal). Equals
   * `populationAt(cid)` when no significance hook is supplied. Test/debug seam.
   */
  significanceOfColony(cid: ColonyId): number {
    if (cid < 0) return 0;
    return this.significanceScore(cid);
  }
  /** APEX_MODE.* of the live boss (Patrol/Hunt/Retreat/Migrate), or -1 when none. */
  bossMode(): number {
    return this.liveId === NIL ? -1 : this.mode;
  }
  /** Live boss territory centre X, or -1 when none (renderers/tests). */
  bossTerritoryX(): number {
    return this.liveId === NIL ? -1 : this.territoryX;
  }
  /** Live boss territory centre Y, or -1 when none (renderers/tests). */
  bossTerritoryY(): number {
    return this.liveId === NIL ? -1 : this.territoryY;
  }
  /** Live boss hunting-territory radius in tiles (config-static), or -1 when none. Render-only. */
  bossTerritoryRadius(): number {
    return this.liveId === NIL ? -1 : this.config.territoryRadius;
  }
  /** Tick until which the live boss avoids nests (0 when none / not avoiding). */
  bossNestAvoidUntil(): number {
    return this.liveId === NIL ? 0 : this.nestAvoidUntil;
  }
  /** Live boss hp, or 0 when none. */
  bossHpVal(): number {
    return this.liveId === NIL ? 0 : (this.entities.hp[this.liveId] as number);
  }
  /** Live boss max hp (the power-scaled pool, or its config hp when unscaled), 0 when none. */
  bossMaxHp(): number {
    return this.liveKind === NO_KIND ? 0 : this.liveMaxHp;
  }
  /** Live boss X, or -1 when none. */
  bossX(): number {
    return this.liveId === NIL ? -1 : (this.entities.posX[this.liveId] as number);
  }
  /** Live boss Y, or -1 when none. */
  bossY(): number {
    return this.liveId === NIL ? -1 : (this.entities.posY[this.liveId] as number);
  }
  /** Live boss entity id, or NIL when none (for renderers/tests). */
  bossEntityId(): EntityId {
    return this.liveId;
  }
  /** Ticks the live boss has been alive, or 0 when none. */
  bossTicksAlive(tick: Tick): number {
    return this.liveId === NIL ? 0 : tick - this.spawnTick;
  }
  /** Colony currently leading the damage race on the live boss, or NIL. */
  topThreatColony(): ColonyId {
    if (this.liveId === NIL) return NIL;
    return this.argmaxDamage();
  }
  /** Damage a colony has dealt to the current live boss (0 if none/no boss). */
  damageBy(cid: ColonyId): number {
    if (cid < 0 || cid >= this.damageByColony.length) return 0;
    return this.damageByColony[cid] as number;
  }

  // --- Alert ring accessors (newest-last in chronological order) --------------

  /** Number of stored alerts (<= alertCap). */
  alertCount(): number {
    return this.alertLen;
  }
  private alertSlot(i: number): number {
    // i in [0, alertLen): 0 = oldest. Map to the physical ring slot.
    const start = (this.alertHead - this.alertLen + this.alertCap) % this.alertCap;
    return (start + i) % this.alertCap;
  }
  alertKindAt(i: number): number {
    return this.alertKind[this.alertSlot(i)] as number;
  }
  alertTickAt(i: number): number {
    return this.alertTick[this.alertSlot(i)] as number;
  }
  alertXAt(i: number): number {
    return this.alertX[this.alertSlot(i)] as number;
  }
  alertYAt(i: number): number {
    return this.alertY[this.alertSlot(i)] as number;
  }

  // --- Defeat-history ring accessors -----------------------------------------

  /** Number of stored defeat-history entries (<= historyCap). */
  defeatCount(): number {
    return this.histLen;
  }
  private histSlot(i: number): number {
    const start = (this.histHead - this.histLen + this.histCap) % this.histCap;
    return (start + i) % this.histCap;
  }
  defeatKindAt(i: number): number {
    return this.histKind[this.histSlot(i)] as number;
  }
  defeatTickAt(i: number): number {
    return this.histTick[this.histSlot(i)] as number;
  }
  defeatVictorAt(i: number): number {
    return this.histVictor[this.histSlot(i)] as number;
  }
  defeatTicksAliveAt(i: number): number {
    return this.histTicksAlive[this.histSlot(i)] as number;
  }
  defeatOutcomeAt(i: number): number {
    return this.histOutcome[this.histSlot(i)] as number;
  }

  // --- Deterministic fingerprint --------------------------------------------

  /**
   * Fold the full apex state into a 32-bit FNV hash: the live boss (kind/hp/x/y/
   * spawnTick or sentinels when none), the per-colony damage ledger, counters,
   * and the FULL alert + defeat-history ring buffers in chronological order.
   */
  hash(): number {
    let h = 0x811c9dc5 >>> 0; // FNV offset basis (local, matches hash.ts)
    h = this.mix(h, this.liveKind);
    h = this.mix(h, this.liveId === NIL ? -1 : (this.entities.hp[this.liveId] as number));
    h = this.mix(h, this.bossX());
    h = this.mix(h, this.bossY());
    h = this.mix(h, this.liveId === NIL ? -1 : this.spawnTick);
    h = this.mix(h, this.spawnCursor);
    // POWER SCALING: fold the scaled boss hp pool ONLY when a power source is wired,
    // so an apex-off / unscaled world hashes byte-identically to before this layer.
    if (this.powerSource !== null) h = this.mix(h, this.liveMaxHp);

    // Territory + mode state machine (all scalars folded; sentinels when no boss
    // so an apex that has never spawned hashes stably).
    const live = this.liveId !== NIL;
    h = this.mix(h, live ? this.territoryX : -1);
    h = this.mix(h, live ? this.territoryY : -1);
    h = this.mix(h, live ? this.mode : -1);
    h = this.mix(h, live ? this.retreatUntil : -1);
    h = this.mix(h, live ? this.lastMigrateTick : -1);
    h = this.mix(h, live ? this.nestProximityTicks : -1);
    h = this.mix(h, live ? this.nestAvoidUntil : -1);
    h = this.mix(h, live ? this.lastDamagedTick : -1);

    for (let cid = 0; cid < this.damageByColony.length; cid++) {
      h = this.mix(h, this.damageByColony[cid] as number);
    }

    h = this.mix(h, this.spawnedTotal);
    h = this.mix(h, this.defeatedTotal);
    h = this.mix(h, this.escapedTotal);
    h = this.mix(h, this.summonedTotal);
    h = this.mix(h, this.devouredTotal);
    h = this.mix(h, this.preyKills);

    const an = this.alertLen;
    h = this.mix(h, an);
    for (let i = 0; i < an; i++) {
      const s = this.alertSlot(i);
      h = this.mix(h, this.alertKind[s] as number);
      h = this.mix(h, this.alertTick[s] as number);
      h = this.mix(h, this.alertX[s] as number);
      h = this.mix(h, this.alertY[s] as number);
    }

    const dn = this.histLen;
    h = this.mix(h, dn);
    for (let i = 0; i < dn; i++) {
      const s = this.histSlot(i);
      h = this.mix(h, this.histKind[s] as number);
      h = this.mix(h, this.histTick[s] as number);
      h = this.mix(h, this.histVictor[s] as number);
      h = this.mix(h, this.histTicksAlive[s] as number);
      h = this.mix(h, this.histOutcome[s] as number);
    }

    // SUMMON PROTECTION: fold the per-entity summon stamps ONLY when protection is
    // wired (column is null by default ⇒ this block is skipped ⇒ byte-identical to
    // the pre-protection hash). Compact: count + (id, tick) of each live stamp in
    // ascending id order (the array IS id order), so identical state ⇒ identical fold.
    if (this.summonTick !== null) {
      const st = this.summonTick;
      let stamped = 0;
      for (let i = 0; i < st.length; i++) if ((st[i] as number) >= 0) stamped++;
      h = this.mix(h, stamped);
      for (let i = 0; i < st.length; i++) {
        const v = st[i] as number;
        if (v < 0) continue;
        h = this.mix(h, i);
        h = this.mix(h, v);
      }
    }
    return h >>> 0;
  }

  /** FNV-1a word mix (low byte first), inlined to keep hash() dependency-free. */
  private mix(hash: number, value: number): number {
    let h = hash;
    h = Math.imul(h ^ (value & 0xff), 0x01000193) >>> 0;
    h = Math.imul(h ^ ((value >>> 8) & 0xff), 0x01000193) >>> 0;
    h = Math.imul(h ^ ((value >>> 16) & 0xff), 0x01000193) >>> 0;
    h = Math.imul(h ^ ((value >>> 24) & 0xff), 0x01000193) >>> 0;
    return h >>> 0;
  }
}
